import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,

} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { toast } from "react-toastify";
import { ENQ_STATUS } from "Constants/StatusConstant";

interface StatusOption {
  value: string;
  label: string;
  color: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
}

interface StatusDropdownProps {
  currentStatus: string;
  itemId: string;
  statusOptions: StatusOption[];
  openStatusModal: (itemId: string, newStatus: string) => void;
  disabled?: boolean;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  currentStatus,
  itemId,
  openStatusModal,
  statusOptions,
  disabled = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const isConverted = currentStatus === "Converted";

  const getCurrentStatusLabel = () =>
    statusOptions.find((opt) => opt.value === currentStatus)?.label ??
    "UNKNOWN";

  const getCurrentStatusColor = () =>
    statusOptions.find((opt) => opt.value === currentStatus)?.color ??
    "#6c757d";

  const getCurrentStatusBackgroundColor = () => {
    const statusOption = statusOptions.find((opt) => opt.value === currentStatus);
    return statusOption?.backgroundColor ?? statusOption?.color ?? "#6c757d";
  };

  const getCurrentStatusBorderColor = () => {
    const statusOption = statusOptions.find((opt) => opt.value === currentStatus);
    return statusOption?.borderColor ?? statusOption?.color ?? "#6c757d";
  };

  const getCurrentStatusTextColor = () => {
    const statusOption = statusOptions.find((opt) => opt.value === currentStatus);
    return statusOption?.textColor ?? "#000000";
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled && !isUpdating && !isConverted) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // 🔒 Business rule: only Lost ↔ In Progress allowed
  const isStatusChangeAllowed = (newStatus: string) => {
    if (isConverted) return false;

    const allowedTransitions: Record<string, string[]> = {
      "In Progress": ["Lost"],
      Lost: ["In Progress"],
    };

    return allowedTransitions[currentStatus]?.includes(newStatus);
  };

  const handleStatusSelect = async (newStatus: string) => {
    if (!isStatusChangeAllowed(newStatus)) return;

    setIsUpdating(true);
    handleClose();

    try {
      await openStatusModal(itemId, newStatus);
      toast.success(
        `Status updated to ${
          statusOptions.find((opt) => opt.value === newStatus)?.label
        }`
      );
    } catch {
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: getCurrentStatusBackgroundColor(),
          color: getCurrentStatusTextColor(),
          border: `1px solid ${getCurrentStatusBorderColor()}`,
          borderRadius: "5px",
          padding: "4px 12px",
          fontSize: "12px",
          fontWeight: "600",
          cursor: disabled || isUpdating ? "not-allowed" : "pointer",
          opacity: disabled || isUpdating ? 0.6 : 1,
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        }}
      >
        <span style={{ whiteSpace: "nowrap" }}>{getCurrentStatusLabel()}</span>
        {currentStatus!==ENQ_STATUS?.CONVERTED && <KeyboardArrowDownIcon sx={{ fontSize: "16px", marginLeft: "4px" }} /> }
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            minWidth: "120px",
            boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
            borderRadius: "10px",
          },
        }}
      >
        {statusOptions
        .filter(option => option.value !== ENQ_STATUS.CONVERTED)
        .map((option) => {
          const allowed = isStatusChangeAllowed(option.value);

          return (
            <MenuItem
              key={option.value}
              disabled={!allowed}
            //   onClick={() => handleStatusSelect(option.value)}
             onClick={() => openStatusModal(itemId, option.value)}
              sx={{
                opacity: allowed ? 1 : 0.4,
                cursor: allowed ? "pointer" : "not-allowed",
                backgroundColor:
                  option.value === currentStatus ? "#f1f5f9" : "transparent",

                "&:hover": {
                  backgroundColor: allowed
                    ? `${option.color}1A`
                    : "transparent",
                },
              }}
            >
              <ListItemIcon>
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: option.color,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "0.80rem",
                  fontWeight: option.value === currentStatus ? 600 : 400,
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default StatusDropdown;
